import type { AppError, ErrorContext, ErrorReporter } from './types';
// import { normalizeSdkError } from './normalizers/sdk';
import { normalizeUnknownError } from './normalizers/unknown';

/**
 * Creates a `processError` function bound to the supplied `reporter`.
 *
 * Call this once at application startup and export the result as your
 * application-wide `processError`. Every normalized error will automatically
 * be forwarded to the reporter — callers never need to log separately.
 *
 * @param reporter - Sink that receives each normalized error.
 * @returns A `processError` function that normalizes and reports errors.
 *
 * @example
 * ```typescript
 * export const processError = createProcessError(myReporter);
 *
 * // Later, at a call site:
 * const appError = processError(caught, { feature: 'policy', operation: 'getPolicy' });
 * showToast({ message: appError.message, type: 'error' });
 * ```
 */
export function createProcessError(reporter: ErrorReporter) {
  /**
   * Normalizes any thrown error into a structured `AppError` and forwards it
   * to the reporter bound at creation time.
   *
   * Tries each normalizer in order:
   *   1. SDK response (policy API - consumer throws non-200 response)
   *   2. Unknown fallback
   *
   * The backend error message is preserved as the source of truth for API
   * errors. The caller decides how to present the resulting `AppError` — this
   * function does not trigger any UI side effects beyond reporting.
   *
   * @param error - The raw caught error (any shape).
   * @param context - Optional metadata about where the error occurred.
   * @returns A normalized `AppError` with a consistent shape.
   */
  return function processError(error: unknown, context?: ErrorContext): AppError {
    const appError =
      // TODO: Add sdk error normalizer here
      // normalizeSdkError(error) ??
      normalizeUnknownError(error);

    if (context) {
      appError.context = context;
    }

    reporter.error(
      appError.message,
      {
        code: appError.code,
        category: appError.category,
        httpStatus: appError.httpStatus,
        context: appError.context,
        requestId: appError.requestId,
      },
      appError.originalError
    );

    return appError;
  };
}
